import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Page } from "@/components/Layout";
import Seo from "@/components/Seo";
import JsonLd from "@/components/JsonLd";
import { api } from "@/lib/api";
import { Plus, Minus, MagnifyingGlass, Question, ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import PageLoader from "@/components/PageLoader";

const TABS = [
  { id: "general", label: "General" },
  { id: "couch", label: "Couch" },
  { id: "forward", label: "Forward" },
  { id: "referral_partners", label: "Referral Partners" },
];

// Module-scoped constants, referenced by JSX props; defined here so they aren't
// recreated on each render (avoids unnecessary framer-motion re-runs).
const FAQ_ACCORDION_INITIAL = { height: 0, opacity: 0 };
const FAQ_ACCORDION_ANIMATE = { height: "auto", opacity: 1 };
const FAQ_ACCORDION_EXIT = { height: 0, opacity: 0 };
const FAQ_ACCORDION_TRANSITION = { duration: 0.28, ease: [0.22, 1, 0.36, 1] };

export default function FAQ() {
  const [active, setActive] = useState("general");
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  // localStorage-backed "I already voted on this" set keeps votes idempotent per browser.
  const [voted, setVoted] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("md-faq-voted") || "{}");
    } catch {
      return {};
    }
  });

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/faqs")
      .then((r) => {
        if (mounted) setFaqs(r.data || []);
      })
      .catch(() => {
        if (mounted) setFaqs([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const submitVote = async (fid, vote) => {
    if (voted[fid]) return; // already voted from this browser
    try {
      const r = await api.post(`/faqs/${fid}/vote`, { vote });
      const { helpful_yes, helpful_no } = r.data || {};
      setFaqs((prev) =>
        prev.map((f) =>
          f.id === fid ? { ...f, helpful_yes, helpful_no } : f
        )
      );
      const next = { ...voted, [fid]: vote };
      setVoted(next);
      try {
        localStorage.setItem("md-faq-voted", JSON.stringify(next));
      } catch (e) {
        // localStorage may be full or unavailable (private mode), vote still works in-memory
        console.warn("[FAQ] localStorage write failed:", e);
      }
    } catch (e) {
      console.error("[FAQ] vote submission failed:", e);
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return faqs
      .filter((f) => f.category === active)
      .filter((f) =>
        !q
          ? true
          : (f.question || "").toLowerCase().includes(q) ||
            (f.answer || "").toLowerCase().includes(q)
      )
      .sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [faqs, active, query]);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.slice(0, 30).map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <Page>
      {faqs.length > 0 && <JsonLd id="jsonld-faq" data={faqJsonLd} />}
      <Seo
        title="Frequently Asked Questions"
        description="Answers about MindDhara therapy, mentorship, pricing, partnerships and how to get started."
      />

      <section className="md-section md-faq-hero md-page-hero" data-testid="faq-hero">
        <div className="md-container text-center">
          <div className="md-label-eyebrow mb-3">Help Centre</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif" data-testid="faq-hero-title">
            Frequently asked <span style={{ color: "var(--md-primary)", fontStyle: "italic" }}>questions</span>
          </h1>
          <p className="text-base mt-5 max-w-2xl mx-auto" style={{ color: "var(--md-text-soft)" }}>
            Straight, useful answers about therapy, mentorship, pricing and partnerships at MindDhara.
          </p>

          {/* Search */}
          <div className="md-faq-search" data-testid="faq-search-wrap">
            <MagnifyingGlass size={18} weight="bold" />
            <input
              type="search"
              placeholder="Search questions, answers and topics…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="faq-search-input"
              aria-label="Search FAQs"
            />
          </div>

          {/* Tabs, sit right below the search bar on the same lime background */}
          <div className="md-faq-tabs" role="tablist" data-testid="faq-tabs">
            {TABS.map((t) => {
              const count = faqs.filter((f) => f.category === t.id).length;
              return (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active === t.id}
                  className={`md-faq-tab ${active === t.id ? "md-faq-tab-active" : ""}`}
                  onClick={() => {
                    setActive(t.id);
                    setOpenId(null);
                  }}
                  data-testid={`faq-tab-${t.id}`}
                >
                  <span>{t.label}</span>
                  {count > 0 && <span className="md-faq-tab-count">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <section className="md-faq-body" data-testid="faq-body">
        <div className="md-container">
          {/* List */}
          <div className="md-faq-list" data-testid="faq-list">
            {loading ? (
              <div className="md-faq-empty">
                <PageLoader sm testid="faq-loading" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="md-faq-empty" data-testid="faq-empty">
                <Question size={32} weight="duotone" />
                <p>No questions match your search yet.</p>
                <p className="md-faq-empty-sub">Try a different keyword or browse another tab.</p>
              </div>
            ) : (
              filtered.map((f) => {
                const open = openId === f.id;
                return (
                  <div
                    key={f.id}
                    className={`md-faq-item ${open ? "md-faq-item-open" : ""}`}
                    data-testid={`faq-item-${f.id}`}
                  >
                    <button
                      className="md-faq-q"
                      onClick={() => setOpenId(open ? null : f.id)}
                      aria-expanded={open}
                      data-testid={`faq-q-${f.id}`}
                    >
                      <span>{f.question}</span>
                      <span className="md-faq-q-ico" aria-hidden>
                        {open ? <Minus size={16} weight="bold" /> : <Plus size={16} weight="bold" />}
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <motion.div
                          key="a"
                          className="md-faq-a-wrap"
                          initial={FAQ_ACCORDION_INITIAL}
                          animate={FAQ_ACCORDION_ANIMATE}
                          exit={FAQ_ACCORDION_EXIT}
                          transition={FAQ_ACCORDION_TRANSITION}
                        >
                          <p className="md-faq-a" data-testid={`faq-a-${f.id}`}>{f.answer}</p>
                          <div className="md-faq-vote" data-testid={`faq-vote-${f.id}`}>
                            {voted[f.id] ? (
                              <span className="md-faq-vote-thanks" data-testid={`faq-vote-thanks-${f.id}`}>
                                Thanks for the feedback!
                              </span>
                            ) : (
                              <span className="md-faq-vote-prompt">Was this helpful?</span>
                            )}
                            <button
                              type="button"
                              className={`md-faq-vote-btn ${voted[f.id] === "yes" ? "md-faq-vote-btn-active" : ""}`}
                              disabled={!!voted[f.id]}
                              onClick={() => submitVote(f.id, "yes")}
                              aria-label="Mark this answer as helpful"
                              data-testid={`faq-vote-yes-${f.id}`}
                            >
                              <ThumbsUp size={14} weight="bold" />
                              <span>{f.helpful_yes || 0}</span>
                            </button>
                            <button
                              type="button"
                              className={`md-faq-vote-btn ${voted[f.id] === "no" ? "md-faq-vote-btn-active" : ""}`}
                              disabled={!!voted[f.id]}
                              onClick={() => submitVote(f.id, "no")}
                              aria-label="Mark this answer as not helpful"
                              data-testid={`faq-vote-no-${f.id}`}
                            >
                              <ThumbsDown size={14} weight="bold" />
                              <span>{f.helpful_no || 0}</span>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>
    </Page>
  );
}
